"use client";

import { c as _c } from "react/compiler-runtime";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import cn from "twsx";

// - plain (default): rows divided by hairlines, chevron trailing.
// - framed: a bordered panel of surface rows with a leading chevron, for
//   record lists that expand to details.
type AccordionVariant = "plain" | "framed";

/** Use when a page has several sections that people expand one at a time. */
const Accordion = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const variant = t1 === undefined ? "plain" : t1;
  const t2 = variant === "framed" && "overflow-hidden rounded-settings-list-view-item border border-border-primary";
  let t3;
  if ($[4] !== className || $[5] !== t2) {
    t3 = cn("group/accordion", t2, className);
    $[4] = className;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t3 || $[9] !== variant) {
    t4 = <AccordionPrimitive.Root data-variant={variant} className={t3} {...props} />;
    $[7] = props;
    $[8] = t3;
    $[9] = variant;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
Accordion.displayName = "Accordion";
const AccordionItem = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("border-b border-border-primary", "group-data-[variant=framed]/accordion:bg-bg-surface group-data-[variant=framed]/accordion:last:border-b-0", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <AccordionPrimitive.Item ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
AccordionItem.displayName = "AccordionItem";

// Layer-list rows (the Atlas map controls): `leading` sits outside the
// trigger for a control such as a visibility checkbox, `icon` keys the row
// (a Swatch), `description` is a second tertiary line, and `trailing` holds a
// count before the chevron.
type AccordionTriggerSlots = {
  leading?: React.ReactNode;
  icon?: React.ReactNode;
  description?: React.ReactNode;
  trailing?: React.ReactNode;
};
const AccordionTrigger = t0 => {
  const $ = _c(27);
  let children;
  let className;
  let description;
  let icon;
  let leading;
  let props;
  let ref;
  let trailing;
  if ($[0] !== t0) {
    ({
      className,
      children,
      leading,
      icon,
      description,
      trailing,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = description;
    $[4] = icon;
    $[5] = leading;
    $[6] = props;
    $[7] = ref;
    $[8] = trailing;
  } else {
    children = $[1];
    className = $[2];
    description = $[3];
    icon = $[4];
    leading = $[5];
    props = $[6];
    ref = $[7];
    trailing = $[8];
  }
  let t1;
  if ($[9] !== leading) {
    t1 = leading && <span className="flex shrink-0 items-center pr-3 group-data-[variant=framed]/accordion:pl-4">{leading}</span>;
    $[9] = leading;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== className) {
    t2 = cn("flex flex-1 cursor-pointer items-center justify-between gap-4 py-3", "text-left text-base font-medium text-text-primary outline-none", "[&[data-state=open]>svg]:rotate-180", "group-data-[variant=framed]/accordion:px-4 group-data-[variant=framed]/accordion:py-4", "group-data-[variant=framed]/accordion:hover:bg-fill-hover-subtle", "group-data-[variant=framed]/accordion:focus-visible:bg-fill-hover-subtle", "group-data-[variant=framed]/accordion:[&>svg]:order-first group-data-[variant=framed]/accordion:[&>svg]:-rotate-90", "group-data-[variant=framed]/accordion:[&[data-state=open]>svg]:rotate-0", className);
    $[11] = className;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== children || $[14] !== description || $[15] !== icon) {
    t3 = icon || description !== undefined ? <span className="flex min-w-0 flex-1 items-center gap-2.5">{icon}<span className="flex min-w-0 flex-col"><span className="truncate">{children}</span>{description !== undefined && <span className="truncate text-sm font-normal leading-4 text-text-tertiary">{description}</span>}</span></span> : children;
    $[13] = children;
    $[14] = description;
    $[15] = icon;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDown className="size-4 shrink-0 text-text-tertiary" />;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== props || $[19] !== ref || $[20] !== t2 || $[21] !== t3 || $[22] !== trailing) {
    t5 = <AccordionPrimitive.Trigger ref={ref} className={t2} {...props}>{t3}{trailing}{t4}</AccordionPrimitive.Trigger>;
    $[18] = props;
    $[19] = ref;
    $[20] = t2;
    $[21] = t3;
    $[22] = trailing;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t1 || $[25] !== t5) {
    t6 = <AccordionPrimitive.Header className="flex items-center">{t1}{t5}</AccordionPrimitive.Header>;
    $[24] = t1;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
};
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
const AccordionContent = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("overflow-hidden text-base", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children) {
    t2 = <div className="pb-4 group-data-[variant=framed]/accordion:px-4">{children}</div>;
    $[7] = children;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props || $[10] !== ref || $[11] !== t1 || $[12] !== t2) {
    t3 = <AccordionPrimitive.Content ref={ref} className={t1} {...props}>{t2}</AccordionPrimitive.Content>;
    $[9] = props;
    $[10] = ref;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };